// ⚠️ **`import type` であること**（値として import すると Phaser 本体が初期バンドルへ入る）。
//    ここは boot 時に読まれる横断モジュールなので、ゲームを開く前に 1.2MB を払わせない。
import type Phaser from 'phaser';
import { getPlayerId, isRegistered } from './playerId';
// ⚠️ **`platform/` からゲーム側を参照する唯一の場所**（下の `GAME_SETTINGS_SLICES` = 設定スライスの登録簿）。
//    ここ以外で platform → games の依存を作らないこと。逆向き（games → platform）は自由。
//    ※ registry は設定スライス（軽い）だけを静的に持ち、ゲーム本体は動的 import なので
//      これを import しても Phaser は落ちてこない。
import { GAMES } from '@/games/registry';

// ポータル横断の設定（localStorage 永続）＋ サウンドの実行時制御 ＋ 設定のサーバー同期。
//
// **所属の切り分け**（2026-08-01 整理）:
//   - ここ（`hongsoft.*`）  = HONGSOFT.JP GAMES 全体で1つ。プレイヤーと端末の属性であって
//                            どのゲームを遊んでいるかに依らない ＝ soundVolume / soundMute / bgmOn
//   - `src/games/<key>/net/gameSettings.ts`（`<key>.*`） = そのゲームでしか意味を持たない
//                            ＝ effectSpeed（牌の裏返し速度）/ routeLineOn（道順ライン）
// プレイヤー識別（`hongsoft.playerId` 等・[playerId.ts](playerId.ts)）と同じ命名規則に揃えてある。
// ※ 旧 `nikakudori.soundVolume` 等からの移行コードは持たない（ユーザー判断・2026-08-01）。
//    既存端末の音量/BGM 設定は一度だけ既定値に戻る。
//
// オリジナル PREFERENCE 画面準拠。※ 投影モード / 言語設定 は Web版では非採用（保留）。

const K_VOLUME = 'hongsoft.soundVolume';
const K_MUTE = 'hongsoft.soundMute';
const K_BGM = 'hongsoft.bgmOn';

export const SETTINGS_DEFAULTS = {
  soundVolume: 0.6,
  soundMute: false,
  bgmOn: true,
} as const;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** サウンド音量 0..1（0 = ミュート）。 */
export function getSoundVolume(): number {
  const v = localStorage.getItem(K_VOLUME);
  if (v === null) return SETTINGS_DEFAULTS.soundVolume;
  const n = Number(v);
  return Number.isFinite(n) ? clamp01(n) : SETTINGS_DEFAULTS.soundVolume;
}
export function setSoundVolume(v: number): void {
  localStorage.setItem(K_VOLUME, String(clamp01(v)));
}

/** マスターミュート（既定: OFF）。ON で全消音＋BGM無効化。 */
export function isSoundMuted(): boolean {
  const v = localStorage.getItem(K_MUTE);
  return v === null ? SETTINGS_DEFAULTS.soundMute : v === '1';
}
export function setSoundMuted(m: boolean): void {
  localStorage.setItem(K_MUTE, m ? '1' : '0');
}

/** BGM を鳴らす設定か（既定: ON）。 */
export function isBgmOn(): boolean {
  const v = localStorage.getItem(K_BGM);
  return v === null ? SETTINGS_DEFAULTS.bgmOn : v === '1';
}
export function setBgmOn(on: boolean): void {
  localStorage.setItem(K_BGM, on ? '1' : '0');
}

/**
 * 全設定を既定値へ戻す（PREFERENCE の「初期設定に戻す」）。
 * ポータル横断ぶんに加えて、登録済みの各ゲームのスライスもリセットする。
 */
export function resetSettings(): void {
  setSoundVolume(SETTINGS_DEFAULTS.soundVolume);
  setSoundMuted(SETTINGS_DEFAULTS.soundMute);
  setBgmOn(SETTINGS_DEFAULTS.bgmOn);
  for (const slice of GAME_SETTINGS_SLICES) slice.reset();
}

/**
 * BGM の基本音量（マスター音量 `soundVolume` に掛かる）。効果音は 0.5〜0.7 で鳴らすため、
 * BGM が効果音と拮抗しないよう控えめにする。
 */
const BGM_VOLUME = 0.4;

/** Phaser の SoundManager（`scene.sound` / `game.sound` と同じ型）。 */
type SoundManager = Phaser.Scene['sound'];

/**
 * BGM を止めている理由。複数が重なりうるので集合で持ち、**全て解除されるまで再開しない**。
 * - `pause`   : 手動の一時停止
 * - `suspend` : タブ非表示 / ウィンドウのフォーカス喪失
 * - `inactive`: ゲームループ休止（ダッシュボード表示中 / スマホ縦向きの回転案内中）
 */
// `result` は「局が終わって結果を見せている間」。2026-08-24 にノノグラムで追加した。
// ⚠️ 既存の `stalemate` はゲーム固有の語がここへ漏れたもの。**新しく足すときは役割の名前で**。
export type BgmHold = 'pause' | 'suspend' | 'inactive' | 'stalemate' | 'result';
const bgmHolds = new Set<BgmHold>();

/**
 * **いま鳴らすべき BGM のサウンドキー**。`applyAudio` が呼ばれるたびに更新され、
 * `holdBgm` / `releaseBgm` はこれを見る。
 *
 * ⚠️ ここに既定値（かつて `'BGM_01'` を直書きしていた）を置かないこと。
 *   横断コードが特定のキーを知っていると、**そのキーを持たないゲームで BGM が無言で鳴らない**
 *   （例外も出ない）という気づけない壊れ方をする。呼び出し側が必ず名乗る形にしてある。
 */
let currentBgmKey: string | null = null;

/**
 * **音量とミュートだけ**を Phaser サウンドへ反映する（BGM には触らない）。
 *
 * ⚠️ **BGM を持たないゲームのための入口**。効果音しか鳴らさないゲームが `applyAudio` を
 *   呼ぶと、存在しない BGM キーを再生しにいってしまう（cards が該当）。
 * 🔴 音量・ミュートの決め方を2か所に書かないため、`applyAudio` も必ずこれを通す。
 */
export function applySoundSettings(scene: Phaser.Scene): void {
  scene.sound.volume = getSoundVolume();
  scene.sound.mute = isSoundMuted();
}

/**
 * 現在の設定を Phaser サウンドへ反映する（音量・ミュート・BGM再生）。
 * ミュートON時は全消音＋BGM停止。BGM が未生成で再生すべき状態なら開始する。
 * 保留(holdBgm)中は鳴らし始めない＝解除時に releaseBgm が続きから再開する。
 *
 * @param bgmKey 鳴らす BGM のサウンドキー。**ゲーム側が持つ定数を渡す**
 *               （Triangle Sweeper は難易度別 = `BGM_FOR_LEVEL[level]`）。
 *               前回と違うキーなら鳴っている曲を止めて差し替える。
 */
export function applyAudio(scene: Phaser.Scene, bgmKey: string): void {
  applySoundSettings(scene);
  const muted = isSoundMuted();

  // 曲が変わったら前の曲を止める（止めないと2曲が重なって鳴る）。
  if (currentBgmKey !== null && currentBgmKey !== bgmKey) {
    const prev = scene.sound.get(currentBgmKey);
    if (prev && (prev.isPlaying || prev.isPaused)) prev.stop();
  }
  currentBgmKey = bgmKey;

  const bgm = scene.sound.get(bgmKey);
  if (muted || !isBgmOn()) {
    // 保留中(=一時停止状態)の BGM も含めて完全に停止する。stop で isPaused も落ちるため、
    // 保留解除時に releaseBgm が勝手に鳴らし直すことはない。
    if (bgm && (bgm.isPlaying || bgm.isPaused)) bgm.stop();
    return;
  }
  if (bgmHolds.size > 0) return;
  if (!bgm) {
    // 🔴 **キャッシュに無い曲は鳴らしにいかない**。`scene.sound.play` は未知のキーで
    //   **例外を投げる**——そして `applyAudio` は各ゲームの `sceneSetup` から呼ばれる、
    //   つまり **全シーンの `create()` の先頭**にいる。
    //   ⚠️ だから失敗の見え方は「BGM が鳴らない」ではなく**画面が丸ごと出ない**（真っ黒）。
    //   ⚠️ 効果音側（各ゲームの `playSe`）は最初からキャッシュを見て黙って抜けるのに、
    //     BGM だけ素通しだった。⇒ 起きうるのは素材の404・綴り違い・
    //     **まだ音を収録していないゲーム**（さめがめが 2026-09-03 に実際そうなった）。
    if (!scene.cache.audio.exists(bgmKey)) return;
    scene.sound.play(bgmKey, { loop: true, volume: BGM_VOLUME });
  } else if (!bgm.isPlaying) bgm.play();
}

/**
 * BGM を一時停止する（再生位置は保持）。同じ理由の重複呼び出しは無害。
 * ※ Phaser の pauseOnBlur は AudioContext ごと suspend する実装で、個々の Sound の
 *   再生状態(isPaused)は書き換えない。よってここでの明示的な pause はフォーカス復帰でも維持される。
 */
export function holdBgm(sound: SoundManager, reason: BgmHold): void {
  bgmHolds.add(reason);
  if (currentBgmKey === null) return; // まだ一度も applyAudio が呼ばれていない
  const bgm = sound.get(currentBgmKey);
  if (bgm?.isPlaying) bgm.pause();
}

/**
 * 保留理由をひとつ解除し、**全ての理由が解除されたら**BGM を続きから再開する。
 * 設定がミュート / BGM再生OFF なら鳴らさない。停止済み・未生成の BGM も再生しない
 * （鳴らし始めの判断は applyAudio の責務）。
 */
export function releaseBgm(sound: SoundManager, reason: BgmHold): void {
  bgmHolds.delete(reason);
  if (bgmHolds.size > 0) return;
  if (currentBgmKey === null) return;
  const bgm = sound.get(currentBgmKey);
  if (!bgm?.isPaused) return;
  if (isSoundMuted() || !isBgmOn()) return;
  bgm.resume();
}

// ============================================================================
// サーバー同期（クロスデバイスの土台）
// ----------------------------------------------------------------------------
// ゲスト（未登録）は純ローカル（localStorage のみ）。ここには一切触れない。
// 登録プレイヤーは D1 に player_id 単位で保存する。localStorage は同期キャッシュ／
// オフライン時のフォールバックとして常に読み書きの一次ソースであり続ける
// （各 getter/setter は同期で使われるため。ネットワークは背後で反映する）。
// ※現状は端末ごとに別 UUID のため実質「サーバー永続」。本認証で複数端末が同一 id を
//   共有すれば、そのまま真のクロスデバイス同期になる。
//
// **ペイロードは `{ common, games: { <gameKey>: {...} } }` のネスト構造**（2026-08-01〜）。
// D1 の `player_settings.settings` は JSON テキスト列なので、この入れ子化にスキーマ変更は要らない
// （0003 のコメントどおり「キーが増減してもスキーマ変更なしで済ませる」ための形）。
// ============================================================================

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** 設定値として往復させられるスカラー。 */
export type SettingValue = number | boolean | string;

/** 1ゲームぶんの設定値（キーはそのゲームの都合。サーバーは中身を解釈しない）。 */
export type GameSettingsValues = Record<string, SettingValue>;

/**
 * ゲーム固有設定の同期スライス。ゲームごとの設定モジュールが1つ輸出し、
 * 下の `GAME_SETTINGS_SLICES` に載せると `/api/settings` の `games.<gameKey>` として往復する。
 */
export interface GameSettingsSlice {
  readonly gameKey: string;
  /** 現在のローカル値を読む（サーバー送信用）。 */
  read(): GameSettingsValues;
  /** サーバー由来の値をローカルへ適用する（欠損キー・型不一致は無視して現状維持）。 */
  apply(values: Partial<GameSettingsValues>): void;
  /** 既定値へ戻す。 */
  reset(): void;
}

/**
 * 同期対象のゲーム固有設定。実体は [games/registry.ts](../../games/registry.ts) の1枚岩で、
 * **ゲームを増やしたら登録簿へ1件足す**だけでこことサーバーは無改修。
 * （登録関数にしないのは、import 順に依存せず「何が同期されるか」が一箇所で読めるようにするため）
 */
const GAME_SETTINGS_SLICES: readonly GameSettingsSlice[] = GAMES.map((g) => g.settings);

/** ポータル横断の設定（どのゲームにも属さない）。 */
export interface CommonSettings {
  soundVolume: number;
  soundMute: boolean;
  bgmOn: boolean;
}

/** サーバー送受信する設定のペイロード（functions/api/settings.ts と一致させる）。 */
export interface SettingsPayload {
  common: CommonSettings;
  games: Record<string, GameSettingsValues>;
}

/** localStorage 上の全設定をまとめて取得（サーバー送信用）。 */
export function getAllSettings(): SettingsPayload {
  const games: Record<string, GameSettingsValues> = {};
  for (const slice of GAME_SETTINGS_SLICES) games[slice.gameKey] = slice.read();
  return {
    common: {
      soundVolume: getSoundVolume(),
      soundMute: isSoundMuted(),
      bgmOn: isBgmOn(),
    },
    games,
  };
}

/**
 * サーバー由来の設定を localStorage へ適用（欠損キーは現状維持）。
 * このバンドルに載っていないゲームの `games.*` は無視する
 * （サーバー側の PUT はゲーム単位でマージするので、消えることはない）。
 */
export function applyAllSettings(s: Partial<SettingsPayload>): void {
  const c = s.common;
  if (c && typeof c === 'object') {
    if (typeof c.soundVolume === 'number') setSoundVolume(c.soundVolume);
    if (typeof c.soundMute === 'boolean') setSoundMuted(c.soundMute);
    if (typeof c.bgmOn === 'boolean') setBgmOn(c.bgmOn);
  }
  const games = s.games;
  if (!games || typeof games !== 'object') return;
  for (const slice of GAME_SETTINGS_SLICES) {
    const v = games[slice.gameKey];
    if (v && typeof v === 'object') slice.apply(v);
  }
}

/** サーバーから設定を取得（記録が無ければ null、失敗時も null）。 */
async function fetchServerSettings(): Promise<Partial<SettingsPayload> | null> {
  try {
    const res = await fetch(`${API_BASE}/settings?id=${encodeURIComponent(getPlayerId())}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { settings: Partial<SettingsPayload> | null };
    return data.settings ?? null;
  } catch {
    return null;
  }
}

/**
 * 現在の設定をサーバーへ保存（登録プレイヤーのみ）。
 * 失敗（オフライン等）は無視する＝次回の 確定/起動時に再同期される。
 */
export async function pushSettingsToServer(): Promise<void> {
  if (!isRegistered()) return;
  try {
    await fetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: getPlayerId(), settings: getAllSettings() }),
    });
  } catch {
    /* オフライン時は無視 */
  }
}

/**
 * 登録プレイヤーの設定をサーバーと突き合わせる（起動時・登録直後に呼ぶ）。
 * - サーバーに記録あり  → それを採用（サーバーを真実とし localStorage を上書き）
 * - サーバーに記録なし  → 現在のローカル設定を初期投入（seed）
 * ゲストは何もしない。
 * 戻り値: サーバー設定を適用したら true（呼び出し側で applyAudio 再適用に使える）。
 */
export async function reconcileSettingsWithServer(): Promise<boolean> {
  if (!isRegistered()) return false;
  const server = await fetchServerSettings();
  if (server) {
    applyAllSettings(server);
    return true;
  }
  await pushSettingsToServer(); // 初回シード
  return false;
}
